import { Component, ErrorInfo, ReactNode } from "react";

import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  render() {
    const { error, errorInfo } = this.state;

    if (!error) return this.props.children;

    const timestamp = new Date().toISOString();
    const url = window.location.href;

    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="size-7 text-destructive shrink-0" />
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                Something went wrong
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Please take a screenshot of this page and send it to the dev
                team.
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-3 font-mono text-xs break-all">
            <div>
              <span className="text-muted-foreground">time: </span>
              <span className="text-foreground">{timestamp}</span>
            </div>
            <div>
              <span className="text-muted-foreground">url: </span>
              <span className="text-foreground">{url}</span>
            </div>
            <div>
              <span className="text-muted-foreground">error: </span>
              <span className="text-destructive font-medium">
                {error.message}
              </span>
            </div>
            {error.stack && (
              <div>
                <span className="text-muted-foreground block mb-1">stack:</span>
                <pre className="text-foreground/70 whitespace-pre-wrap leading-relaxed overflow-auto max-h-48">
                  {error.stack}
                </pre>
              </div>
            )}
            {errorInfo?.componentStack && (
              <div>
                <span className="text-muted-foreground block mb-1">
                  component stack:
                </span>
                <pre className="text-foreground/70 whitespace-pre-wrap leading-relaxed overflow-auto max-h-48">
                  {errorInfo.componentStack}
                </pre>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="size-4 mr-2" />
              Reload page
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              <RotateCcw className="size-4 mr-2" />
              Go to home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
